import { UseQueryResult } from '@tanstack/react-query';
import { PROJECT_ID, PROJECT_NAME } from 'routes/BudgetModule/config/constants';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { ProjectQueryKey } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import { useProjectQuery } from 'routes/BudgetModule/helpers/hooks/useCustomQuery';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import { getDataPreProcessingUrlMiddleware } from 'routes/BudgetModule/shared/getDataMiddlewares';
import { queryToSearch } from 'routes/Explore/components/Sentence/helpers';
import { HttpError, RQAPICallWithData } from 'store/APIClient';

export type ProjectWithInscriptionT = Omit<InscriptionT, typeof PROJECT_ID> & {
    [PROJECT_NAME]: string;
};

/**
 * Custom hook to fetch and return a list of projects with budget inscriptions
 * data based on the given budget proposal ventilation and applied filters.
 *
 * @param budgetProposal - The budget proposal used to determine the by.
 * @param filters - Array of filters to apply to the query.
 */

export const useGetProjectsWithInscriptions = (
    budgetProposal: BudgetProposalT,
    filters: QueryFilterT[],
    cacheKey: ProjectQueryKey,
): UseQueryResult<ProjectWithInscriptionT[], HttpError> => {
    const queryParams = queryToSearch({
        filters: filters,
        metrics: [],
        dimensions: [],
    });
    const endpointUrl = `inscriptions_projects/projects_with_budget_inscriptions_data?${queryParams}&budget_by=${budgetProposal.by}`;
    return useProjectQuery<ProjectWithInscriptionT[], HttpError>(
        cacheKey,
        async () => {
            const response = await RQAPICallWithData<ProjectWithInscriptionT[]>(
                {
                    endpoint: endpointUrl,
                    method: 'GET',
                    preProcessingUrlCallback: getDataPreProcessingUrlMiddleware,
                },
            );
            return response.content.data;
        },
    );
};
